import { useState, useCallback } from 'react'
import { TaskService, TaskServiceZod } from '@duck/api-definitions'
import { z } from 'zod'

interface ValidationResult {
  isValid: boolean
  errors: Record<string, string>
}

interface UseTaskValidationReturn {
  validateTask: (taskData: Partial<TaskService.Task>, isNewTask: boolean) => ValidationResult
  validateField: (field: keyof TaskService.Task, value: string | number, isNewTask: boolean) => string | null
  clearErrors: () => void
  fieldErrors: Record<string, string>
}

export const useTaskValidation = (): UseTaskValidationReturn => {
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  const validateTask = useCallback((taskData: Partial<TaskService.Task>, isNewTask: boolean): ValidationResult => {
    const errors: Record<string, string> = {}

    try {
      TaskServiceZod.schemas.TaskBase.parse(taskData)
    } catch (error) {
      if (error instanceof z.ZodError) {
        error.errors.forEach((err) => {
          const fieldPath = err.path.join('.')
          errors[fieldPath] = err.message
        })
      }
    }

    // Custom validation for feedbackPayload - must be valid JSON if provided
    if (taskData.feedbackPayload && typeof taskData.feedbackPayload === 'string' && taskData.feedbackPayload.trim() !== '') {
      try {
        JSON.parse(taskData.feedbackPayload)
      } catch {
        errors['feedbackPayload'] = 'Feedback payload must be valid JSON format'
      }
    }

    setFieldErrors(errors)

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    }
  }, [])

  const validateField = useCallback((field: keyof TaskService.Task, value: string | number, isNewTask: boolean): string | null => {
    try {
      // Task is a ZodIntersection (TaskUpdate & { id: string }), so we need to handle validation differently
      // We'll validate by creating a partial object and checking it against the appropriate schema
      const fieldData = { [field]: value }

      TaskServiceZod.schemas.TaskBase.parse(fieldData)

      // Custom validation for feedbackPayload - must be valid JSON if provided
      if (field === 'feedbackPayload' && value && typeof value === 'string' && value.trim() !== '') {
        try {
          JSON.parse(value)
        } catch {
          const errorMessage = 'Feedback payload must be valid JSON format'
          setFieldErrors(prev => ({
            ...prev,
            [field]: errorMessage
          }))
          return errorMessage
        }
      }

      // Clear any existing error for this field
      setFieldErrors(prev => {
        const { [field]: _, ...rest } = prev
        return rest
      })

      return null
    } catch (error) {
      if (error instanceof z.ZodError) {
        const errorMessage = error.errors[0]?.message || 'Invalid value'
        
        setFieldErrors(prev => ({
          ...prev,
          [field]: errorMessage
        }))
        
        return errorMessage
      }
      return 'Invalid value'
    }
  }, [])

  const clearErrors = useCallback(() => {
    setFieldErrors({})
  }, [])

  return {
    validateTask,
    validateField,
    clearErrors,
    fieldErrors
  }
}
